import React, { useState, useEffect, useMemo, useRef } from 'react';
import { styled } from '@mui/material/styles';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import { useDispatch, useSelector } from 'react-redux';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import { getServiceRequestById, getServiceRequestMilestones } from '../../../../actions/service_request';
import { TicketExecutionHeader } from './TicketExecutionHeader';
import { motion } from 'framer-motion';
import Checklist from './Checklist';
import { MilestoneSubStatusOrder, MilestoneType } from '../../../Ticket/TicketController';
import Boq from './Boq';
import { isPavMilestone } from '../../../../features/pav/pavConfig';

const Accordion = styled((props) => (
    <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    '&:not(:last-child)': {
        borderBottom: 0,
    },
    '&:before': {
        display: 'none',
    },
}));

const AccordionSummary = styled((props) => (
    <MuiAccordionSummary
        expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '1.1rem', color: 'white' }} />}
        {...props}
    />
))(({ theme }) => ({
    backgroundColor:
        theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, .05)'
            : 'rgba(0, 0, 0, .03)',
    flexDirection: 'row-reverse',
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
        transform: 'rotate(90deg)',
    },
    '& .MuiAccordionSummary-content': {
        marginLeft: theme.spacing(1),
    },
}));


const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    padding: theme.spacing(2),
    borderTop: '1px solid rgba(0, 0, 0, .125)',
}));


export const colorLookup = {
    green: '#10B981',
    orange: '#F59E0B',
    red: '#EF4444',
}

export const signoff = { field_title: 'Upload SignOff Images with Sign and Stamp', key: 'upload_signoff_images', input_type: 'fileupload' };

export default function SubHeader({ isSupportTicket, memoizedChatDetails, id, defaulOpentChecklist = false }) {
    const dispatch = useDispatch();
    const [headerColor, setHeaderColor] = useState({});
    const serviceRequestData = useSelector((state) => state.serviceRequest.serviceRequestData);
    const [showBoq, setShowBoq] = useState(false);
    const accordionRef = useRef(null);

    const [selectedMilestones, setSelectedMilestones] = useState(null);
    const [milestoneTasksState, setMilestoneTasksState] = useState({});
    const serviceRequestMilestones = useSelector((state) => state.serviceRequest.serviceRequestMilestones);

    useEffect(() => {
        if (serviceRequestMilestones && memoizedChatDetails?.id && serviceRequestMilestones[memoizedChatDetails.id]) {
            const milestones = serviceRequestMilestones[memoizedChatDetails.id];
            const newTaskState = {};

            milestones.forEach(milestone => {
                if (milestone.tasks) {
                    newTaskState[milestone.id] = milestone.tasks.map(task => ({
                        ...task,
                        isSaved: task.input_type === 'FILE' ? !!task.files?.length : !!task.responses?.length,
                        files: task.files || []
                    }));
                }
            });

            setMilestoneTasksState(newTaskState);
        }
    }, [serviceRequestMilestones, memoizedChatDetails?.id]);

    const updateTaskInMilestone = (milestoneId, taskIndex, updatedTask) => {
        setMilestoneTasksState(prev => ({
            ...prev,
            [milestoneId]: prev[milestoneId]?.map((task, index) =>
                index === taskIndex ? { ...task, ...updatedTask } : task
            ) || []
        }));
    };

    const memoizedMilestones = useMemo(() => {
        if (!serviceRequestMilestones || !memoizedChatDetails?.id || !serviceRequestMilestones[memoizedChatDetails.id]) {
            return [];
        }

        let milestones = serviceRequestMilestones[memoizedChatDetails.id];
        milestones = milestones.sort((a, b) => {
            const statusOrderA = MilestoneSubStatusOrder[a.milestone_status] || 0;
            const statusOrderB = MilestoneSubStatusOrder[b.milestone_status] || 0;
            return statusOrderA - statusOrderB;
        });

        if (milestones.length > 0 && !selectedMilestones) {
            setSelectedMilestones(milestones[0].id);
        }

        milestones.forEach(milestone => {
            const currentTasks = milestoneTasksState[milestone.id] || milestone.tasks || [];

            const isTaskAlreadyUploaded = task => {
                if (!task) return false;

                if (task.input_type === 'FILE') {
                    return Array.isArray(task.files) && task.files.length > 0 && task.isSaved;
                }

                if (task.input_type === 'MULTI_SELECT') {
                    return Array.isArray(task.responses) && task.responses.length > 0 && task.isSaved;
                }

                return Boolean(task.isSaved);
            };

            const visibleTasks = currentTasks.filter(task => {
                return task.visibility_conditions?.every(condition => {
                    const { task_id, operator, value } = condition;
                    const referencedTask = currentTasks.find(t => t.id === task_id);
                    if (!referencedTask) return true;

                    const responseValue = referencedTask.responses && referencedTask.responses.length > 0
                        ? referencedTask.responses.map(response => response?.value ? response.value : response)
                        : [];

                    switch (operator) {
                        case '=': return responseValue.includes(value);
                        case '!=': return !responseValue.includes(value);
                        case 'IN': return Array.isArray(value) && responseValue.some(item => value.includes(item));
                        case 'NOT_IN': return Array.isArray(value) && !responseValue.some(item => value.includes(item));
                        case 'LIKE': return responseValue.some(item => String(item).includes(value));
                        case 'NOT_LIKE': return !responseValue.some(item => String(item).includes(value));
                        case 'CONTAINS': return responseValue.includes(value);
                        case 'NOT_CONTAINS': return !responseValue.includes(value);
                        case 'EXISTS': return responseValue.length > 0;
                        case 'NOT_EXISTS': return responseValue.length === 0;
                        case 'REGEX': return responseValue.some(item => new RegExp(value).test(String(item)));
                        case 'NOT_REGEX': return !responseValue.some(item => new RegExp(value).test(String(item)));
                        case 'IS_NULL': return responseValue.length === 0;
                        case 'IS_NOT_NULL': return responseValue.length > 0;
                        default: return true;
                    }
                }) || true;
            });

            const requiredTasks = visibleTasks.filter(task => task.is_required);
            const areAllTasksCompleted = milestone?.execution_flow_required === false ? true :
                (requiredTasks.length > 0 ? requiredTasks.every(isTaskAlreadyUploaded) : true);

            setHeaderColor(prev => ({
                ...prev,
                [milestone.id]: areAllTasksCompleted ? '#4ade80' : '#fb923c'
            }));
        });

        return milestones.map(milestone => ({
            ...milestone,
            tasks: milestoneTasksState[milestone.id] || milestone.tasks || []
        }));
    }, [serviceRequestMilestones, memoizedChatDetails?.id, selectedMilestones, milestoneTasksState]);

    useEffect(() => {
        if (!serviceRequestMilestones[memoizedChatDetails.id]) {
            dispatch(getServiceRequestMilestones({ id: memoizedChatDetails.id }));
        }
    }, [serviceRequestMilestones, dispatch, memoizedChatDetails.id]);


    useEffect(() => {
        if (id && !serviceRequestData[id]) {
            dispatch(getServiceRequestById({ ticketId: id }));
        }
    }, [serviceRequestData, dispatch, id]);

    const memoizedServiceRequest = useMemo(() => {
        let s = {};
        if (serviceRequestData && serviceRequestData[id]) {
            s = serviceRequestData[id];
        }
        return s;
    }, [serviceRequestData, id]);

    const currentMilestone = useMemo(() => {
        let m = {};
        if (memoizedMilestones && selectedMilestones) {
            m = memoizedMilestones.find((milestone) => milestone.id === selectedMilestones);
        }
        return m;
    }, [memoizedMilestones, selectedMilestones]);

    useEffect(() => {
        if (isPavMilestone(currentMilestone)) setShowBoq(false);
    }, [currentMilestone]);

    const currentHeaderColor = currentMilestone && memoizedMilestones && memoizedMilestones.length === 1 && headerColor[currentMilestone.id] ? headerColor[currentMilestone.id] : '#7090B040';

    const isMigrateTicket = memoizedServiceRequest.milestones && memoizedServiceRequest.milestones.every((milestone) => milestone.type === MilestoneType.LOGISTICS && milestone.execution_flow_required === false);

    const toggleBoq = () => {
        setShowBoq(!showBoq);
    };

    if (isMigrateTicket) return null;

    return <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
        className={`w-full z-20 overflow-hidden rounded-lg shadow-lg ${isSupportTicket ? 'absolute block' : 'chatSuHeaderTop'}`}
    >
        <Accordion className='bg-white rounded-lg'>
            <AccordionSummary
                expandIcon={null}
                sx={{
                    padding: '0',
                    margin: '0',
                    minHeight: '2.5rem',
                    maxHeight: '2.5rem',
                    height: '100%',
                    background: currentHeaderColor,
                    width: '100%',
                    '& .MuiAccordionSummary-content': {
                        margin: 0,
                        width: '100%',
                        height: '100%',
                        padding: '0',
                        display: 'flex',
                        alignItems: 'stretch'
                    }
                }}
            >
                {selectedMilestones && <TicketExecutionHeader
                    memoizedMilestones={memoizedMilestones}
                    selectedMilestones={selectedMilestones}
                    setSelectedMilestones={setSelectedMilestones}
                    headerColor={headerColor}
                    onAddBoqClick={toggleBoq}
                    accordionRef={accordionRef}
                    defaulOpentChecklist={defaulOpentChecklist}
                    agents={memoizedServiceRequest?.agents}
                />}
            </AccordionSummary>
            <div className='shadow-lg h-full max-h-full flex flex-col overflow-hidden'>
                <AccordionDetails sx={{ padding: '0', flex: '1 1 auto', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <div className="flex flex-col h-full overflow-hidden">
                        <div className="flex-1 overflow-auto">
                            {showBoq ? <Boq toggleBoq={toggleBoq} currentMilestone={currentMilestone} memoizedServiceRequest={memoizedServiceRequest} /> : <Checklist
                                setHeaderColor={setHeaderColor}
                                memoizedServiceRequest={memoizedServiceRequest}
                                currentMilestone={currentMilestone}
                                id={id}
                                updateTaskInMilestone={updateTaskInMilestone}
                                milestoneTasksState={milestoneTasksState}
                            />}
                        </div>
                    </div>
                </AccordionDetails>
            </div>
        </Accordion>
    </motion.div>
}
